import React from 'react';
import { ArrowRight, Zap, Users, Bot, BarChart3, Megaphone, Settings, Star, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { usePodPanel } from '../../pages/LandingPage';

const PodShowcase = () => {
  const { openPodPanel } = usePodPanel();
  
  const podFeatures = [
    { icon: Users, label: '40+ Virtual Departments', color: 'text-cyan-400' },
    { icon: Bot, label: 'AI Agents & Voice Bots', color: 'text-emerald-400' },
    { icon: Megaphone, label: 'Cinematic Ad System™', color: 'text-violet-400' },
    { icon: BarChart3, label: 'CRM & BDC-X Support', color: 'text-green-400' },
    { icon: Settings, label: 'Automation & Funnels', color: 'text-pink-400' },
    { icon: Star, label: '"Best of Best" Visibility', color: 'text-orange-400' },
  ];

  const serviceTypes = [
    { title: 'DFY', subtitle: 'Done-For-You', description: 'Our team handles everything' },
    { title: 'DWY', subtitle: 'Done-With-You', description: 'Collaborate with our experts' },
    { title: 'DIY', subtitle: 'Do-It-Yourself', description: '3000+ AI tools at your fingertips' },
  ];

  return (
    <section className="py-16 md:py-24 bg-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 rounded-full px-5 py-2 mb-6">
            <Zap className="w-5 h-5 text-emerald-400" />
            <span className="text-white font-semibold">Included with Your $120K Co-Op Funding</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            The AI Horse-Powered{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">POD™</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-cyan-400 font-semibold mb-4">
            Projects On Demand • Publish On Demand • Promote On Demand
          </p>
          
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            Access a complete business growth engine with 40+ virtual departments, AI-powered tools, 
            and managed services — all backed by your $120,000 in Co-Op Funds.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-14">
          {/* Left Side - POD Dashboard Image */}
          <div className="order-2 lg:order-1">
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-emerald-500/30 rounded-2xl blur-3xl transform scale-105" />
              
              {/* Image Container */}
              <div className="relative rounded-2xl overflow-hidden border-2 border-cyan-500/30 shadow-2xl shadow-cyan-500/20">
                <img 
                  src="/pod_dashboard.png" 
                  alt="AI Horse-Powered POD Dashboard - Projects On Demand" 
                  className="w-full h-auto"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent pointer-events-none" />
                
                {/* Floating Label */}
                <div className="absolute bottom-4 left-4 bg-slate-900/90 backdrop-blur-sm border border-cyan-500/30 rounded-lg px-4 py-2">
                  <p className="text-cyan-400 font-semibold text-sm">Your POD Command Center</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Features & Service Types */}
          <div className="order-1 lg:order-2 space-y-8">
            {/* Key Features */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                What's Inside Your POD
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {podFeatures.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div 
                      key={index}
                      className="flex items-center gap-3 bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 hover:border-cyan-500/30 transition-colors"
                    >
                      <Icon className={`w-5 h-5 ${feature.color} flex-shrink-0`} />
                      <span className="text-slate-300 text-sm font-medium">{feature.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Three Ways to Get It Done */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Three Ways to Execute</h3>
              <div className="grid grid-cols-3 gap-3">
                {serviceTypes.map((service, index) => (
                  <div 
                    key={index}
                    className="text-center bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 hover:border-emerald-500/30 transition-colors"
                  >
                    <p className="text-2xl font-bold text-emerald-400 mb-1">{service.title}</p>
                    <p className="text-white text-sm font-semibold mb-1">{service.subtitle}</p>
                    <p className="text-slate-500 text-xs">{service.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-lg text-slate-400 mb-6">
            No silos. No waiting. Just momentum — <span className="text-emerald-400 font-semibold">on demand.</span>
          </p>
          
          <Button
            onClick={openPodPanel}
            size="lg"
            className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white font-bold px-10 py-6 text-lg rounded-xl transition-all hover:shadow-xl hover:shadow-cyan-500/30 group"
          >
            Explore the POD Platform
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PodShowcase;
